/**
 * The signed-in account, and what happens when the session ends.
 *
 * Two jobs:
 *  1. Establish the session once on load, so a reload keeps the reader where
 *     they were instead of restarting the flow. Usually that means asking
 *     `GET /auth/session`; on a page that arrived with a `?token=` it means
 *     waiting for the redemption instead — see `arrivingToken` below.
 *  2. Own the global 401. Every API call funnels its 401 here (see
 *     `setUnauthorizedHandler` in api/client.ts); this provider marks the
 *     session gone and sends the reader to `/anmelden?weiter=<where they were>`
 *     so the sign-in does not cost them their place.
 */
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react'

import { api, setUnauthorizedHandler } from '@/api/client'
import { redeemOnce, type TokenKind } from '@/features/auth/redeem'
import { injectedPrintPayload } from '@/features/report/print'
import { ROUTES, RETURN_PARAM, currentHref, matchRoute, navigate } from '@/lib/router'
import type { SessionInfo, WorkspaceRef } from '@/api/types'

export type SessionStatus = 'loading' | 'signedIn' | 'signedOut'

interface SessionValue {
  status: SessionStatus
  session: SessionInfo | null
  workspaces: WorkspaceRef[]
  isFounder: boolean
  /** Adopt the session a magic link or invite just minted. */
  adopt: (session: SessionInfo) => void
  /** Re-read the session (after creating a workspace, changing a data mode …). */
  refresh: () => Promise<void>
  signOut: () => Promise<void>
}

const SessionContext = createContext<SessionValue | null>(null)

/** Where to send someone who just lost (or never had) a session. */
export function signInHref(from: string = currentHref()): string {
  const match = matchRoute(from)
  if (match.name === 'signIn' || match.name === 'invite' || from === ROUTES.home) {
    return ROUTES.signIn
  }
  return `${ROUTES.signIn}?${RETURN_PARAM}=${encodeURIComponent(from)}`
}

/**
 * The token this page load arrived with, if it arrived with one.
 *
 * Why the provider cares. Both jobs below want to know "who is signed in?", and
 * on a token surface there are two ways to ask: probe `GET /auth/session`, or
 * redeem the token. Asking both at once is asking the same question twice and
 * letting whichever answers last win — and the probe is sent *before* the
 * redemption's cookie exists, so its answer is 401 and it is wrong the moment
 * it is sent. When that stale 401 lands after the 200 it replaces the freshly
 * signed-in session with "signed out" and, because the reader has by then been
 * moved off the token surface, the global-401 handler bounces them to
 * `/anmelden?weiter=…`. The link is single-use, so it is already spent: the
 * reader is signed out holding a dead link. Measured in Chromium against a real
 * backend, this lost the race on 3 of 3 dev loads and 1 of 6 production loads.
 *
 * So the two are ordered instead of raced: with a token present the redemption
 * *is* the session lookup, and the probe only runs if it fails.
 *
 * This costs no extra request and cannot burn the link. `redeemOnce` keys the
 * in-flight promise by token at module scope, so the provider and the token
 * screen wait on the very same POST (`features/auth/redeem.ts`).
 */
function arrivingToken(): { kind: TokenKind; token: string } | null {
  const match = matchRoute(currentHref())
  const token = match.search.get('token')
  if (!token) return null
  if (match.name === 'signIn') return { kind: 'magic-link', token }
  if (match.name === 'invite') return { kind: 'invite', token }
  return null
}

export function SessionProvider({ children }: { children: ReactNode }) {
  // A PDF-export render carries an injected report and has no session by
  // design; it starts (and stays) signed out rather than probing.
  const [status, setStatus] = useState<SessionStatus>(() =>
    injectedPrintPayload() ? 'signedOut' : 'loading',
  )
  const [session, setSession] = useState<SessionInfo | null>(null)
  // Guards the redirect: a burst of parallel 401s must produce one navigation.
  const redirecting = useRef(false)

  const adopt = useCallback((next: SessionInfo) => {
    redirecting.current = false
    setSession(next)
    setStatus('signedIn')
  }, [])

  const refresh = useCallback(async () => {
    try {
      adopt(await api.session())
    } catch {
      setSession(null)
      setStatus('signedOut')
    }
  }, [adopt])

  // The one session lookup on load. Written out rather than `void refresh()` so
  // the state updates land in the promise callbacks, not synchronously in the
  // effect body (react-hooks/set-state-in-effect).
  //
  // Skipped entirely when the PDF export injected a report: that render has no
  // session by design, and an unanswerable probe would be the only network call
  // on a page whose whole point is that it makes none.
  useEffect(() => {
    if (injectedPrintPayload()) return
    let active = true
    // A page that arrived with a token gets its session from the redemption,
    // never from a probe running alongside it. See `arrivingToken`.
    const arriving = arrivingToken()
    const settling = arriving
      ? redeemOnce(arriving.kind, arriving.token).catch(() => api.session())
      : api.session()
    settling
      .then((next) => {
        if (!active) return
        setSession(next)
        setStatus('signedIn')
      })
      .catch(() => {
        if (!active) return
        setSession(null)
        setStatus('signedOut')
      })
    return () => {
      active = false
    }
  }, [])

  useEffect(
    () =>
      setUnauthorizedHandler(() => {
        setSession(null)
        setStatus('signedOut')
        const here = currentHref()
        const match = matchRoute(here)
        // Never navigate away from the two token surfaces: a redirect there
        // would throw away the `?token=` the reader just arrived with.
        if (match.name === 'signIn' || match.name === 'invite') return
        if (redirecting.current) return
        redirecting.current = true
        navigate(signInHref(here), { replace: true })
      }),
    [],
  )

  const signOut = useCallback(async () => {
    try {
      await api.signOut()
    } catch {
      // A session that is already gone is the outcome we wanted anyway.
    }
    setSession(null)
    setStatus('signedOut')
    redirecting.current = false
    navigate(ROUTES.signIn)
  }, [])

  const value = useMemo<SessionValue>(
    () => ({
      status,
      session,
      workspaces: session?.workspaces ?? [],
      isFounder: session?.global_role === 'founder',
      adopt,
      refresh,
      signOut,
    }),
    [status, session, adopt, refresh, signOut],
  )

  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>
}

export function useSession(): SessionValue {
  const value = useContext(SessionContext)
  if (!value) throw new Error('useSession muss innerhalb von <SessionProvider> verwendet werden.')
  return value
}

/** Remembers the workspace a founder last looked at, so `/` lands sensibly. */
const LAST_WORKSPACE_KEY = 'baupilot.workspace'

export function rememberWorkspace(id: string): void {
  try {
    window.localStorage.setItem(LAST_WORKSPACE_KEY, id)
  } catch {
    /* private mode, disabled storage — the URL is the source of truth anyway */
  }
}

export function recalledWorkspace(): string | null {
  try {
    return window.localStorage.getItem(LAST_WORKSPACE_KEY)
  } catch {
    return null
  }
}
